import { FlowTrackerSteps, MANUAL_TRANSFER_FAQ } from 'common/constants';
import {
  ATBeaconControl,
  ATBeaconControlGroup,
  ATBeaconNotification,
} from 'components/common/beacon/beaconRegistration';
import FlowTracker from 'components/common/FlowTracker/FlowTracker';
import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Accordion from 'src/components/common/Accordion/Accordion';
import {
  setManualExternalAccount,
  setTransferType,
} from 'src/features/transfer/transferSlice';
import { RootState } from 'src/state/store';
import { AccountType, AccountTypeDisplayNames, TransferTypeValue } from 'utils/constants';
import { formatBalanceDisplay, formatBalanceInput } from 'utils/numberUtils';
import { IManualExternalAccountState } from '../../transferSlice.types';
import './ManualTransfer.scss';

interface ManualTransferProps {
  accountTypes: AccountType[];
  setIsStepValid: (isEnabled: boolean) => void;
  showValidation: boolean;
  setShowValidation: (isEnabled: boolean) => void;
}

const ManualTransfer: FC<ManualTransferProps> = (props) => {
  const { showValidation, setShowValidation, accountTypes } = props;
  const dispatch = useDispatch();
  const manualExternalAccount = useSelector(
    (state: RootState) => state.transfer.manualExternalAccount,
  );
  const transferType = useSelector((state: RootState) => state.transfer.transferType);

  const [initialBalanceValue, setInitialBalanceValue] = useState<string>('');

  useEffect(() => {
    if (manualExternalAccount?.balance.amount != null) {
      const formattedValue = formatBalanceDisplay(manualExternalAccount.balance.amount);
      setInitialBalanceValue(`$${formattedValue}`);
    }
  }, [manualExternalAccount]);

  const handleSelectionChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    field: keyof IManualExternalAccountState,
  ) => {
    const value = e.target.value;

    if (field === 'balance') {
      return;
    }

    if (field === 'accountType') {
      dispatch(
        setManualExternalAccount({
          ...manualExternalAccount,
          accountType: parseInt(value) || null,
        }),
      );
    } else {
      dispatch(
        setManualExternalAccount({
          ...manualExternalAccount,
          [field]: value,
        }),
      );
    }
  };

  const handleBalanceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInitialBalanceValue(e.target.value);
  };

  const handleBalanceBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const parsedValue = formatBalanceInput(rawValue);
    const formattedValue = formatBalanceDisplay(parsedValue);
    setInitialBalanceValue(`$${formattedValue}`);
    dispatch(
      setManualExternalAccount({
        ...manualExternalAccount,
        balance: {
          amount: parsedValue,
          currency: 'USD',
        },
      }),
    );
  };

  const handleSelectTransferType = (type: TransferTypeValue) => {
    dispatch(setTransferType(type));
  };

  return (
    <div className="content-container b-grid">
      <div className="content-left b-grid-item--full-col b-xs:12-col b-sm:12-col b-md:8-col b-lg:8-col">
        <div className="b-grid Internal-account">
          <div className="b-grid-item--full-col b-xs:12-col b-sm:12-col b-md:12-col b-lg:12-col">
            {showValidation && (
              <div
                id="summary"
                className="b-grid-item--full-col b-xs:12-col b-sm:12-col b-md:12-col b-lg:12-col b-padding-block-end--micro--jumbo"
              >
                <ATBeaconNotification
                  type="inline"
                  variant="warning"
                  dismissible
                  onBeaconEventDismiss={() => setShowValidation(false)}
                >
                  Please complete all fields to move forward
                </ATBeaconNotification>
              </div>
            )}
            <div className="b-grid-item--full-col b-xs:12-col b-sm:12-col b-md:12-col b-lg:12-col">
              <label className="b-typography-sans-serif--headline--100 b-font-weight-bold">
                Enter your external account information
              </label>
            </div>
            <div className="b-padding--organism-normal">
              <form id="manual-transfer-form" noValidate className="b-flow-space">
                <ATBeaconControl>
                  <label htmlFor="institution-name" slot="label">
                    Financial institution name
                  </label>
                  <input
                    type="text"
                    name="institutionName"
                    id="institution-name"
                    value={manualExternalAccount?.institutionName}
                    onChange={(e) => handleSelectionChange(e, 'institutionName')}
                    required={true}
                    data-testid="institution-name"
                  />
                  <span slot="error">Please enter Financial institution name</span>
                </ATBeaconControl>
                <ATBeaconControl className="account-type-control">
                  <label htmlFor="account-type" slot="label">
                    Type of account
                  </label>
                  <select
                    onChange={(e) => handleSelectionChange(e, 'accountType')}
                    value={manualExternalAccount?.accountType ?? ''}
                    id="account-type"
                    data-testid="account-type"
                    name="accountType"
                    required={true}
                  >
                    <option key="default" value="">
                      Select
                    </option>
                    {accountTypes.map((type) => (
                      <option
                        value={type.toString()}
                        key={`${AccountTypeDisplayNames[type]}-dSuffix`}
                      >
                        {AccountTypeDisplayNames[type]}
                      </option>
                    ))}
                  </select>
                  <span slot="error">Please select Account Type</span>
                </ATBeaconControl>
                <ATBeaconControl>
                  <label htmlFor="owner-name" slot="label">
                    Account owner name(s)
                  </label>
                  <input
                    type="text"
                    name="ownerName"
                    id="owner-name"
                    value={manualExternalAccount?.ownerName}
                    onChange={(e) => handleSelectionChange(e, 'ownerName')}
                    required={true}
                    data-testid="owner-name"
                  />
                  <span slot="error">Please enter Account owner name(s)</span>
                </ATBeaconControl>
                {manualExternalAccount?.accountType === AccountType.JOINT_TENANT && (
                  <ATBeaconControl>
                    <label htmlFor="joint-owner-name" slot="label">
                      Joint Owner Name(s)
                    </label>
                    <input
                      type="text"
                      name="jointOwnerName"
                      id="joint-owner-name"
                      value={manualExternalAccount?.jointOwnerName}
                      onChange={(e) => handleSelectionChange(e, 'jointOwnerName')}
                      required={true}
                      data-testid="joint-owner-name"
                    />
                    <span slot="error">Please enter Joint Owner Name(s)</span>
                  </ATBeaconControl>
                )}
                <ATBeaconControl>
                  <label htmlFor="dollar-amount" slot="label">
                    Approximate dollar amount
                  </label>
                  <input
                    type="text"
                    name="balance"
                    id="dollar-amount"
                    value={initialBalanceValue}
                    onChange={handleBalanceChange}
                    onBlur={handleBalanceBlur}
                    required={true}
                    data-testid="dollarAmount"
                  />
                  <span slot="error">Please enter Approximate dollar amount</span>
                </ATBeaconControl>
                <ATBeaconControl>
                  <label htmlFor="account-number" slot="label">
                    Account number
                  </label>
                  <input
                    type="text"
                    name="accountNumber"
                    id="account-number"
                    value={manualExternalAccount?.accountNumber}
                    onChange={(e) => handleSelectionChange(e, 'accountNumber')}
                    required={true}
                    data-testid="account-number"
                  />
                  <span slot="error">Please enter Account number</span>
                </ATBeaconControl>
                <div>
                  <ATBeaconControlGroup>
                    <div slot="label">
                      Do you want to make a full or partial transfer of your assets?
                    </div>
                    <ATBeaconControl inline>
                      <label htmlFor="full-transfer" slot="label">
                        Full transfer
                      </label>
                      <input
                        type="radio"
                        id="full-transfer"
                        name="transferType"
                        value={TransferTypeValue.FullTransfer}
                        checked={transferType === TransferTypeValue.FullTransfer}
                        onChange={() =>
                          handleSelectTransferType(TransferTypeValue.FullTransfer)
                        }
                      />
                    </ATBeaconControl>
                    <ATBeaconControl inline>
                      <label htmlFor="partial-transfer" slot="label">
                        Partial transfer
                      </label>
                      <input
                        type="radio"
                        id="partial-transfer"
                        name="transferType"
                        value={TransferTypeValue.PartialTransfer}
                        checked={transferType === TransferTypeValue.PartialTransfer}
                        onChange={() =>
                          handleSelectTransferType(TransferTypeValue.PartialTransfer)
                        }
                      />
                    </ATBeaconControl>
                  </ATBeaconControlGroup>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <div className="b-grid-item--full-col b-xs:12-col b-sm:12-col b-md:4-col b-lg:4-col">
        <div className="flow-tracker-container">
          <FlowTracker currentFlowStep={1} items={FlowTrackerSteps} />
        </div>
        <div className="faq-container">
          <Accordion items={MANUAL_TRANSFER_FAQ} />
        </div>
      </div>
    </div>
  );
};

export default ManualTransfer;
